// „Vítr“ ve vínovém panelu přihlášení – firemní znak WANET (převzato z Witry / hero banneru wanet.cz).
// Nic nestahuje, nic neukládá. Skrytá záložka stojí, „omezit pohyb“ = jeden nehybný snímek, pomalý počítač si ubere čárek.

export function startWind(canvas: HTMLCanvasElement): () => void {
  const ctx = canvas.getContext('2d');
  const art = canvas.parentElement;
  if (!ctx || !art) return () => undefined;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  let W = 0;
  let H = 0;
  let U = 1;
  interface Part {
    x: number;
    y: number;
    life: number;
    hi: boolean;
    w: number;
    len: number;
    hx: number[];
    hy: number[];
  }
  let parts: Part[] = [];
  const mouse = { x: -999, y: -999 };
  const weather = { dir: 250 + Math.random() * 40, wind: 2.5, gust: 3.5 };
  let curDir = weather.dir;
  let curSpeed = weather.wind;
  let running = false;
  let last = 0;
  let acc = 0;
  let ema = 16;
  let checkAt = 0;
  let timer = 0;
  const STEP = 1 / 60;

  function gust(): void {
    const turn = (30 + Math.random() * 90) * (Math.random() < 0.5 ? -1 : 1);
    weather.dir = (weather.dir + turn + 360) % 360;
    weather.wind = 1.2 + Math.random() * 3.6;
    weather.gust = weather.wind + 0.5 + Math.random() * 2;
    timer = window.setTimeout(gust, 45000 + Math.random() * 30000);
  }

  function spawn(p: Part): Part {
    p.x = Math.random() * W;
    p.y = Math.random() * H;
    p.life = 60 + Math.random() * 220;
    p.hi = Math.random() < 0.14;
    p.w = Math.random() < 0.4 ? 0 : Math.random() < 0.6 ? 1 : 2;
    p.len = (14 + Math.random() * 30) | 0;
    p.hx = [p.x];
    p.hy = [p.y];
    return p;
  }

  function newPart(): Part {
    return spawn({ x: 0, y: 0, life: 0, hi: false, w: 0, len: 0, hx: [], hy: [] });
  }

  function resize(): boolean {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    W = canvas.clientWidth;
    H = canvas.clientHeight;
    if (!W || !H || !ctx) return false;
    const sc = Math.max(0.6, Math.min(dpr, Math.sqrt(2.0e6 / (W * H))));
    canvas.width = Math.round(W * sc);
    canvas.height = Math.round(H * sc);
    ctx.setTransform(sc, 0, 0, sc, 0, 0);
    U = H / 900;
    const save = (navigator as unknown as { connection?: { saveData?: boolean } }).connection?.saveData;
    const n = Math.min(save ? 250 : 500, Math.round((W * H) / (save ? 3400 : 2000)));
    parts = [];
    for (let i = 0; i < n; i++) parts.push(newPart());
    return true;
  }

  function sim(t: number): void {
    const dd = ((weather.dir - curDir + 540) % 360) - 180;
    const cil = weather.wind + (weather.gust - weather.wind) * Math.max(0, Math.sin(t / 6.5)) * 0.6;
    curDir += dd * 0.004;
    curSpeed += (cil - curSpeed) * 0.01;
    const base = ((curDir + 180) * Math.PI) / 180;
    const speed = (0.9 + curSpeed * 0.7) * U;
    const R = 170 * U;
    const R2 = R * R;
    const r = 56 * U;
    const r2 = r * r;
    const bx = mouse.x;
    const by = mouse.y;
    for (const p of parts) {
      const px = p.x / U;
      const py = p.y / U;
      const n = Math.sin(px * 0.0021 + t * 0.13) + Math.sin(py * 0.0031 - t * 0.11) + Math.sin((px + py) * 0.0013 + t * 0.07);
      const a = base + n * 0.5;
      let vx = Math.sin(a) * speed;
      let vy = -Math.cos(a) * speed;
      const dx = p.x - bx;
      const dy = p.y - by;
      const d2 = dx * dx + dy * dy;
      if (d2 < R2) {
        const d = Math.sqrt(d2) || 1;
        let f = 1 - d / R;
        f = f * f * 6 * U;
        vx += (dx / d) * f;
        vy += (dy / d) * f;
      }
      let nx = p.x + vx;
      let ny = p.y + vy;
      const ex = nx - bx;
      const ey = ny - by;
      const e2 = ex * ex + ey * ey;
      if (e2 < r2) {
        const e = Math.sqrt(e2) || 1;
        nx = bx + (ex / e) * r;
        ny = by + (ey / e) * r;
      }
      p.x = nx;
      p.y = ny;
      p.life--;
      if (p.life > 0) {
        p.hx.push(nx);
        p.hy.push(ny);
      }
      if (p.hx.length > p.len || p.life <= 0) {
        p.hx.shift();
        p.hy.shift();
      }
      if (p.hx.length < 2 && p.life <= 0) spawn(p);
      else if (nx < -60 || nx > W + 60 || ny < -60 || ny > H + 60) spawn(p);
    }
  }

  const WIDTHS = [0.7, 1.15, 1.7];
  const COLORS = ['rgba(243,238,230,0.16)', 'rgba(255,255,255,0.42)'];
  function draw(): void {
    if (!ctx) return;
    ctx.clearRect(0, 0, W, H);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    for (let c = 0; c < 2; c++)
      for (let w = 0; w < 3; w++) {
        ctx.beginPath();
        for (const p of parts) {
          if ((p.hi ? 1 : 0) !== c || p.w !== w || p.hx.length < 2) continue;
          ctx.moveTo(p.hx[0] as number, p.hy[0] as number);
          for (let j = 1; j < p.hx.length; j++) ctx.lineTo(p.hx[j] as number, p.hy[j] as number);
        }
        ctx.strokeStyle = COLORS[c] as string;
        ctx.lineWidth = (WIDTHS[w] as number) * Math.max(0.8, U);
        ctx.stroke();
      }
  }

  function frame(now: number): void {
    if (!running) return;
    const dt = last ? Math.min(0.05, (now - last) / 1000) : STEP;
    last = now;
    ema += (dt * 1000 - ema) * 0.05;
    acc += dt;
    let k = 0;
    while (acc >= STEP && k < 3) {
      sim(now / 1000);
      acc -= STEP;
      k++;
    }
    if (acc >= STEP) acc = 0;
    draw();
    if (now > checkAt) {
      checkAt = now + 2000;
      if (ema > 20 && parts.length > 120) parts.length = Math.round(parts.length * 0.85);
    }
    requestAnimationFrame(frame);
  }

  function start(): void {
    if (running || reduced || document.hidden || !W) return;
    running = true;
    last = 0;
    acc = 0;
    requestAnimationFrame(frame);
  }
  function stop(): void {
    running = false;
  }

  if (!resize()) return () => undefined;
  if (reduced) {
    for (let k = 0; k < 160; k++) sim(k / 60);
    draw();
    return () => undefined;
  }
  const onMove = (e: PointerEvent) => {
    const b = canvas.getBoundingClientRect();
    mouse.x = e.clientX - b.left;
    mouse.y = e.clientY - b.top;
  };
  const onLeave = () => {
    mouse.x = mouse.y = -999;
  };
  let rt = 0;
  const onResize = () => {
    window.clearTimeout(rt);
    rt = window.setTimeout(() => {
      if (resize()) start();
      else stop();
    }, 150);
  };
  const onVis = () => {
    if (document.hidden) stop();
    else start();
  };
  art.addEventListener('pointermove', onMove);
  art.addEventListener('pointerleave', onLeave);
  window.addEventListener('resize', onResize);
  document.addEventListener('visibilitychange', onVis);
  timer = window.setTimeout(gust, 20000 + Math.random() * 30000);
  start();
  return () => {
    stop();
    window.clearTimeout(timer);
    art.removeEventListener('pointermove', onMove);
    art.removeEventListener('pointerleave', onLeave);
    window.removeEventListener('resize', onResize);
    document.removeEventListener('visibilitychange', onVis);
  };
}
